import { useState, useEffect, useRef } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapPin, AlertTriangle, CheckCircle2, Navigation, Smartphone, User, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const API = process.env.REACT_APP_BACKEND_URL;

export default function VisitHeatmap() {
  const { user } = useAuth();
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markersRef = useRef([]);
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, alerts: 0, gps: 0, nfc: 0 });

  useEffect(() => { fetchHeatmap(); }, []);

  useEffect(() => {
    if (!mapRef.current || points.length === 0) return;
    if (mapInstance.current) {
      markersRef.current.forEach(m => m.remove());
      markersRef.current = [];
    } else {
      mapInstance.current = L.map(mapRef.current, { zoomControl: true }).setView([51.5074, -0.1278], 12);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap'
      }).addTo(mapInstance.current);
    }

    const bounds = [];
    points.forEach(p => {
      const isAlert = p.alert;
      const isCheckIn = p.event === 'check_in';
      const color = isAlert ? '#ef4444' : isCheckIn ? '#00A6A0' : '#3b82f6';
      const size = isAlert ? 12 : 8;

      const icon = L.divIcon({
        className: 'custom-marker',
        html: `<div style="width:${size}px;height:${size}px;border-radius:50%;background:${color};border:2px solid white;box-shadow:0 0 4px rgba(0,0,0,0.3);"></div>`,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
      });

      const marker = L.marker([p.lat, p.lon], { icon }).addTo(mapInstance.current);
      marker.bindPopup(
        `<div style="font-size:12px;min-width:150px;">
          <strong>${p.caregiver}</strong><br/>
          <span style="color:#888;">for</span> <strong style="color:#00A6A0;">${p.client}</strong><br/>
          <span style="color:#888;">${p.time}</span><br/>
          <span style="display:inline-block;padding:2px 6px;border-radius:4px;font-size:10px;background:${color}20;color:${color};">
            ${isCheckIn ? 'CLOCK IN' : 'CLOCK OUT'}
          </span>
          ${p.distance != null ? `<br/><span style="color:${isAlert ? '#ef4444' : '#00A6A0'};font-size:10px;">${p.distance}m from client ${isAlert ? '(ALERT)' : ''}</span>` : ''}
          <br/><span style="color:#888;font-size:10px;">Verified: ${p.method.toUpperCase()}</span>
        </div>`
      );
      markersRef.current.push(marker);
      bounds.push([p.lat, p.lon]);
    });

    if (bounds.length > 0) {
      mapInstance.current.fitBounds(bounds, { padding: [30, 30] });
    }
  }, [points]);

  const fetchHeatmap = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/visit-audit/heatmap`, { withCredentials: true });
      setPoints(data.points || []);
      const pts = data.points || [];
      setStats({
        total: pts.length,
        alerts: pts.filter(p => p.alert).length,
        gps: pts.filter(p => p.method === 'gps').length,
        nfc: pts.filter(p => p.method === 'nfc').length,
      });
    } catch { setPoints([]); }
    setLoading(false);
  };

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="visit-heatmap-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-heading font-bold text-foreground flex items-center gap-2">
              <MapPin className="w-6 h-6 text-[#00A6A0]" />Visit Heatmap
            </h1>
            <p className="text-sm text-muted-foreground mt-1">GPS visualization of all clock-in/out locations</p>
          </div>
          <Button size="sm" variant="outline" className="border-border" onClick={fetchHeatmap} data-testid="refresh-heatmap">
            <RefreshCw className="w-4 h-4 mr-1" />Refresh
          </Button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="glass-card border-border">
            <CardContent className="p-3 text-center">
              <p className="text-xl font-bold text-foreground">{stats.total}</p>
              <p className="text-[10px] text-muted-foreground">GPS Points</p>
            </CardContent>
          </Card>
          <Card className="glass-card border-[#00A6A0]/30">
            <CardContent className="p-3 text-center">
              <div className="flex items-center justify-center gap-1 mb-0.5">
                <Navigation className="w-3 h-3 text-[#00A6A0]" />
                <p className="text-xl font-bold text-[#00A6A0]">{stats.gps}</p>
              </div>
              <p className="text-[10px] text-muted-foreground">GPS Verified</p>
            </CardContent>
          </Card>
          <Card className="glass-card border-[#8B5CF6]/30">
            <CardContent className="p-3 text-center">
              <div className="flex items-center justify-center gap-1 mb-0.5">
                <Smartphone className="w-3 h-3 text-[#8B5CF6]" />
                <p className="text-xl font-bold text-[#8B5CF6]">{stats.nfc}</p>
              </div>
              <p className="text-[10px] text-muted-foreground">NFC Verified</p>
            </CardContent>
          </Card>
          <Card className="glass-card border-red-500/30">
            <CardContent className="p-3 text-center">
              <div className="flex items-center justify-center gap-1 mb-0.5">
                <AlertTriangle className="w-3 h-3 text-red-400" />
                <p className="text-xl font-bold text-red-400">{stats.alerts}</p>
              </div>
              <p className="text-[10px] text-muted-foreground">Distance Alerts</p>
            </CardContent>
          </Card>
        </div>

        {/* Legend */}
        <div className="flex gap-4 items-center text-xs text-muted-foreground">
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-[#00A6A0] inline-block"></span>Clock In</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-blue-500 inline-block"></span>Clock Out</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-red-500 inline-block"></span>Distance Alert (&gt;500m)</span>
        </div>

        {/* Map */}
        <Card className="glass-card border-border overflow-hidden">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-[500px] bg-muted/30">
                <p className="text-muted-foreground">Loading heatmap...</p>
              </div>
            ) : points.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-[500px] bg-muted/30">
                <MapPin className="w-12 h-12 text-muted-foreground mb-3 opacity-30" />
                <p className="text-muted-foreground">No GPS data available</p>
                <p className="text-xs text-muted-foreground mt-1">Seed demo data from Visit Audit Trail to see the heatmap</p>
              </div>
            ) : (
              <div ref={mapRef} style={{ height: '500px', width: '100%' }} data-testid="heatmap-container" />
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
